import { Cross2Icon, PlusIcon } from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  PublicFieldView,
  PublicFormSubmitRequest,
  PublicFormView,
} from "client/openapi";
import { Button, ButtonColor } from "components/Button";
import { ReactNode, useEffect, useState } from "react";
import FormFieldInput, { AnswerValue, initialAnswer } from "./FormFieldInput";

type Answers = Record<string, AnswerValue>;

export type PublicFormAnswers = Omit<PublicFormSubmitRequest, "token">;

function startingAnswers(fields?: PublicFieldView[] | null): Answers {
  const answers: Answers = {};
  (fields || []).forEach((field) => {
    answers[field.field] = initialAnswer(field);
  });
  return answers;
}

/** Keep what was typed for questions that still exist; start new ones fresh. */
function merge(previous: Answers, fields?: PublicFieldView[] | null): Answers {
  const fresh = startingAnswers(fields);
  Object.keys(fresh).forEach((key) => {
    if (key in previous) fresh[key] = previous[key];
  });
  return fresh;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

/**
 * The form a parent, student or tutor fills in. The same component draws the
 * builder's preview, so what the admin sees is what the family sees.
 */
export default function PublicForm({
  view,
  preview = false,
  submitting = false,
  errors = {},
  notice,
  paymentSlot,
  beforeSubmit,
  onSubmit,
  onAddPayrollBank,
}: {
  view: PublicFormView;
  preview?: boolean;
  submitting?: boolean;
  errors?: Record<string, string>;
  notice?: string;
  paymentSlot?: ReactNode;
  /** Shown just above Submit (a website inquiry's robot check). */
  beforeSubmit?: ReactNode;
  onSubmit?: (answers: PublicFormAnswers) => void;
  onAddPayrollBank?: () => void;
}) {
  // A parent's or a tutor's own questions (a view only ever has one kind).
  const own =
    view.recipient_type === Auth0AccountRole.ORG_TUTOR ? "tutor" : "parent";
  const ownFields = view.parent_fields || view.tutor_fields;
  const [answers, setAnswers] = useState<Answers>(() =>
    startingAnswers(ownFields)
  );
  const [students, setStudents] = useState<Record<number, Answers>>(() =>
    Object.fromEntries(
      (view.student_blocks || []).map((block) => [
        block.student_id,
        startingAnswers(block.fields),
      ])
    )
  );
  const [newStudents, setNewStudents] = useState<Answers[]>([]);
  const [termsAgreed, setTermsAgreed] = useState(false);

  // A tick only counts for the wording it was given to.
  useEffect(() => setTermsAgreed(false), [view.terms_text]);

  // When the form changes underneath (a newer version, or the builder's
  // preview), keep every answer to a question that still exists.
  useEffect(() => {
    setAnswers((prev) => merge(prev, view.parent_fields || view.tutor_fields));
    setStudents((prev) =>
      Object.fromEntries(
        (view.student_blocks || []).map((block) => [
          block.student_id,
          merge(prev[block.student_id] || {}, block.fields),
        ])
      )
    );
    setNewStudents((prev) =>
      view.allow_add_students
        ? prev.map((answers) => merge(answers, view.new_student_fields))
        : []
    );
  }, [view]);

  const isStudentForm = view.recipient_type === Auth0AccountRole.ME;
  const hasErrors = Object.keys(errors).length > 0;

  function submit() {
    if (preview || !onSubmit) return;
    onSubmit({
      revision: view.revision as number,
      parent: (own === "parent" ? answers : {}) as any,
      tutor: (own === "tutor" ? answers : {}) as any,
      students: [
        ...Object.entries(students).map(([studentId, values]) => ({
          student_id: Number(studentId),
          values: values as any,
        })),
        ...newStudents.map((values) => ({
          student_id: null,
          values: values as any,
        })),
      ],
      terms_agreed: termsAgreed,
    });
  }

  return (
    // Not a <form>: only the Submit button submits, never Enter in a field.
    <div>
      {view.org_name && (
        <p className="text-sm font-medium text-gray-500">{view.org_name}</p>
      )}
      <h1 className="text-2xl font-semibold text-gray-900 mt-1">
        {view.form_name}
      </h1>
      {view.intro && (
        <p className="mt-3 text-base text-gray-700 whitespace-pre-line">
          {view.intro}
        </p>
      )}
      {notice && (
        <p className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
          {notice}
        </p>
      )}

      {ownFields && ownFields.length > 0 && (
        <Section title="About you">
          {ownFields.map((field) => (
            <FormFieldInput
              key={field.field}
              id={`${own}-${field.field}`}
              field={field}
              value={answers[field.field] ?? null}
              onChange={(value) =>
                setAnswers((prev) => ({ ...prev, [field.field]: value }))
              }
              error={errors[`${own}:${field.field}`]}
              disabled={submitting}
            />
          ))}
        </Section>
      )}

      {((view.student_blocks && view.student_blocks.length > 0) ||
        view.allow_add_students) && (
        <Section title={isStudentForm ? "About you" : "Your students"}>
          {(view.student_blocks || []).map((block) => (
            <div
              key={block.student_id}
              className={
                isStudentForm
                  ? ""
                  : "rounded-xl border border-gray-200 bg-white p-4 mb-4"
              }
            >
              {!isStudentForm && (
                <h3 className="text-base font-semibold text-gray-900 mb-3">
                  {block.name}
                </h3>
              )}
              {block.fields.map((field) => (
                <FormFieldInput
                  key={field.field}
                  id={`student-${block.student_id}-${field.field}`}
                  field={field}
                  value={students[block.student_id]?.[field.field] ?? null}
                  onChange={(value) =>
                    setStudents((prev) => ({
                      ...prev,
                      [block.student_id]: {
                        ...prev[block.student_id],
                        [field.field]: value,
                      },
                    }))
                  }
                  error={errors[`student:${block.student_id}:${field.field}`]}
                  disabled={submitting}
                />
              ))}
            </div>
          ))}

          {newStudents.map((answers, index) => (
            <div
              key={index}
              className="relative rounded-xl border border-gray-200 bg-white p-4 mb-4"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-900">
                  New student
                </h3>
                <button
                  type="button"
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
                  aria-label="Remove this student"
                  onClick={() =>
                    setNewStudents((prev) => prev.filter((_, i) => i !== index))
                  }
                  disabled={submitting}
                >
                  <Cross2Icon />
                </button>
              </div>
              {(view.new_student_fields || []).map((field) => (
                <FormFieldInput
                  key={field.field}
                  id={`new-${index}-${field.field}`}
                  field={field}
                  value={answers[field.field] ?? null}
                  onChange={(value) =>
                    setNewStudents((prev) =>
                      prev.map((a, i) =>
                        i === index ? { ...a, [field.field]: value } : a
                      )
                    )
                  }
                  error={errors[`new:${index}:${field.field}`]}
                  disabled={submitting}
                />
              ))}
            </div>
          ))}

          {view.allow_add_students && (
            <button
              type="button"
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 px-4 py-3 text-base text-gray-700 hover:border-gray-400"
              onClick={() =>
                setNewStudents((prev) => [
                  ...prev,
                  startingAnswers(view.new_student_fields),
                ])
              }
              disabled={submitting}
            >
              <PlusIcon /> Add a student
            </button>
          )}
          {errors["new"] && (
            <p className="mt-1 text-sm text-red-600" data-form-error>
              {errors["new"]}
            </p>
          )}
        </Section>
      )}

      {view.payroll_bank && (
        <Section title="Bank for payroll">
          {view.payroll_bank.set_up ? (
            <p className="text-sm text-gray-700">
              Your payroll bank is set up.
            </p>
          ) : view.payroll_bank.ready ? (
            <Button
              type="button"
              color={ButtonColor.PURPLE}
              onClick={onAddPayrollBank}
              disabled={preview || submitting}
            >
              Add your bank for payroll
            </Button>
          ) : (
            <p className="text-sm text-gray-700">
              Finish creating your Tadpole account first, then come back to this
              link.
            </p>
          )}
          {errors["payroll"] && (
            <p className="mt-2 text-sm text-red-600" data-form-error>
              {errors["payroll"]}
            </p>
          )}
        </Section>
      )}

      {view.terms_text !== null && view.terms_text !== undefined && (
        <Section title="Terms">
          <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 whitespace-pre-line">
            {view.terms_text}
          </div>
          <label className="mt-3 flex items-start gap-3 text-base text-gray-900">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
              checked={termsAgreed}
              onChange={(e) => setTermsAgreed(e.target.checked)}
              disabled={submitting}
            />
            <span>I agree to the terms above.</span>
          </label>
          {errors["terms"] && (
            <p className="mt-1 text-sm text-red-600" data-form-error>
              {errors["terms"]}
            </p>
          )}
        </Section>
      )}

      {paymentSlot}
      {beforeSubmit}

      {hasErrors && (
        <p className="mt-6 text-sm text-red-600">
          Some answers need fixing. They are marked above.
        </p>
      )}

      <div className="mt-6">
        <Button
          type="button"
          onClick={submit}
          color={preview ? ButtonColor.GRAY : ButtonColor.GREEN}
          extraClasses="w-full !py-3 !text-base"
          disabled={preview || submitting}
        >
          {submitting ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </div>
  );
}
